import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Polygon, MultiPolygon, FeatureCollection, Point } from '@turf/helpers';
import { Observable, Subject, Subscription, firstValueFrom, forkJoin } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { MapHelperService } from 'src/services/map-helper.service';
import { LayerAPIService } from 'src/app/features/shared/services/API/LayersAPI.service';
import * as wellknown from 'wellknown';
import { ProspectsMapSectionService, LayerReloadRequest } from '../../../services/prospects-map-section.service';
import { PolygonSelectorWithLevelsComponent } from 'src/app/features/map-features/ui/organisms/polygon-selector-with-levels/components/polygon-selector-with-levels/polygon-selector-with-levels.component';
import { PolygonSelectorServiceType } from 'src/app/features/map-features/ui/organisms/polygon-selector-with-levels/helpers/EnumSelectorServices';
import { HierarchyLevel, SelectionHistoryItem, SelectionChangeEvent } from 'src/app/features/map-features/ui/organisms/polygon-selector-with-levels/models/polygon-selector-with-levels.models';
import { PolygonSelectorComponent } from 'src/app/features/map-features/ui/organisms/polygon-selector/components/polygon-selector/polygon-selector.component';
import { EnumPolygonFillType } from 'src/app/features/map-features/ui/organisms/polygon-selector/helpers/enums/EnumPolygonFillType.enum';
import { CategoryFiltersDTO } from 'src/app/features/shared/models/CategoryFilters/CategoryFilters';

@Component({
  selector: 'prospects-section',
  templateUrl: './prospects-section.component.html',
  styleUrls: ['./prospects-section.component.css'],
})
export class ProspectsMapComponent implements OnInit, OnDestroy {

  @ViewChild('polygonSelector') polygonSelector: PolygonSelectorWithLevelsComponent;
  @ViewChild('agebSelector') agebSelector: PolygonSelectorComponent;

  isInitializing: boolean = true;
  hasInitialized: boolean = false; 
  isLoadingZone: boolean = false;

  hierarchyLevels: HierarchyLevel[] = [];
  serviceType = PolygonSelectorServiceType.Potential_Zones;
  isPolygonSelectionWithevelsActive: boolean = true;
  simpleSelectorPolygonFillType = EnumPolygonFillType.Solid;
  agebCloropetConfig = this.prospectsMapSectionService.agebCloropetConfig;

  simpleSelectorPrefixAdd: string = 'prospects-agebs-selector';

  private zoneRequestId: number = 0;
  private prospectRequestId: number = 0;
  private destroyZoneLoad$: Subject<void> = new Subject<void>();
  private destroyProspectLoad$: Subject<void> = new Subject<void>();

  subscription_isVisible: Subscription;
  subscription_layerReload: Subscription;
  subscription_prospectDataChanged: Subscription;

  selectedHistory: SelectionHistoryItem[] = [];
  firstZone: { name: string, isSelected: boolean } = { name: 'Región Seleccionada', isSelected: false };
  secondZone: { name: string, isSelected: boolean, id: string } = { name: 'Zona Seleccionada', isSelected: false, id: null };

  agebsGeoJson: FeatureCollection<Polygon | MultiPolygon> = null;
  prospectsGeoJson: FeatureCollection<Point> = null;

  locationHash: Map<string, { lng: number, lat: number }> = new Map();

  constructor(
    private mapHelperService: MapHelperService,
    public prospectsMapSectionService: ProspectsMapSectionService,
    private layersAPIService: LayerAPIService
  ) {
    this.subscription_isVisible = prospectsMapSectionService.isVisible.asObservable().subscribe( isVisible => {
      if(isVisible){
        this.initializeComponent();
      } else if (this.hasInitialized) {
        this.isInitializing = true;
        this.resetZones();
      }
    });

    this.subscription_layerReload = prospectsMapSectionService.layerReloadTrigger$.subscribe((request: LayerReloadRequest) => {
      if (this.secondZone.isSelected && this.secondZone.id) {
        this.prospectsMapSectionService.applyActiveCategoryVisuals();
        this.reloadProspectLayer(request.categoryId, request.filters);
      }
    });

    //recarga la capa forzando el bypass de cache para no mostrar datos desactualizados
    this.subscription_prospectDataChanged = prospectsMapSectionService.getProspectDataChanged().subscribe(() => {
      const request = this.prospectsMapSectionService.getCurrentLayerReloadRequest();
      if (request) {
        this.reloadProspectLayer(request.categoryId, request.filters, true);
      }
    });
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
    this.cancelPendingZoneLoad();
    this.prospectsMapSectionService.setIsProspectsVisible(false);
    this.prospectsMapSectionService.destroyLayers();

    this.subscription_isVisible?.unsubscribe();
    this.subscription_layerReload?.unsubscribe();
    this.subscription_prospectDataChanged?.unsubscribe();
  }

  async initializeComponent(): Promise<void> {
    this.isInitializing = true;
    this.hierarchyLevels = await this.prospectsMapSectionService.loadLayers(this.simpleSelectorPrefixAdd);

    this.isInitializing = false;
    this.hasInitialized = true;
    this.mapHelperService.mapResize();
  }

  onHierarchySelectionChange(event: SelectionChangeEvent): void {
    this.selectedHistory = event.history;

    if (event.history.length === 0) {
      this.resetZones();
      return;
    }

    if (event.history.length >= 1) {
      const firstSelection = event.history[0];
      this.firstZone = {
        name: firstSelection.description || 'Región Seleccionada',
        isSelected: true
      };
    }else{
      this.firstZone = { name: 'Región Seleccionada', isSelected: false };
    }

    if (event.history.length >= 2) {
      const secondSelection = event.history[1];
      this.secondZone = {
        name: secondSelection.description || 'Zona Seleccionada',
        isSelected: true,
        id: secondSelection.selectedId
      };

      if (this.polygonSelector) {
        this.isPolygonSelectionWithevelsActive = false;
      }
      this.loadZoneAndActivateSelector(secondSelection.selectedId);
    }else{
      this.secondZone = { name: 'Zona Seleccionada', isSelected: false, id: null};
      this.resetZoneData();
    }
  }

  loadZoneAndActivateSelector(zoneId: string): void {
    this.cancelPendingZoneLoad();
    const requestId = ++this.zoneRequestId;
    const prospectRequestId = ++this.prospectRequestId;

    this.agebsGeoJson = null;
    this.prospectsGeoJson = null;
    this.prospectsMapSectionService.clearLocations();

    this.isLoadingZone = true;
    this.prospectsMapSectionService.setLoading(true);
    this.prospectsMapSectionService.setZoneId(zoneId);

    const filters: CategoryFiltersDTO[] = [{ Key: 'Zone', Value: zoneId }, ...this.prospectsMapSectionService.getCategoryFilters()];
    const lastLevelKey = this.hierarchyLevels[this.hierarchyLevels.length - 1].key;
    const categoryId = this.prospectsMapSectionService.getActiveCategory()?.id;

    this.prospectsMapSectionService.applyActiveCategoryVisuals();
    forkJoin([
      this.GetLayerByIdLocationZoneGeoJSON(lastLevelKey, zoneId),
      this.GetLayerByIdLocationCategoryGeoJSON(categoryId, filters)
    ]).pipe(takeUntil(this.destroyZoneLoad$)).subscribe({
      next: ([agebGeoJson, prospectGeoJson]) => {
        if (requestId !== this.zoneRequestId) {
          return;
        }
        this.agebsGeoJson = agebGeoJson as FeatureCollection<Polygon | MultiPolygon>;

        if (prospectRequestId === this.prospectRequestId) {
          this.prospectsGeoJson = prospectGeoJson;
        }
        this.paintZoneLayers(requestId, prospectRequestId)
      },
      error: (error) => {
        if (requestId === this.zoneRequestId) {
          this.isLoadingZone = false;
          this.prospectsMapSectionService.setLoading(false);
        }
        console.log('error cargando zona de prospectos', error);
      }
    });
  }

  private async paintZoneLayers(requestId: number, prospectRequestId: number): Promise<void> {
    try {
      if (this.agebSelector) {
        await this.agebSelector.activate(false);
        if (requestId !== this.zoneRequestId) {
          return;
        }

        if (this.agebsGeoJson) {
          this.agebSelector.loadGeoJson(this.agebsGeoJson);
        }
        this.agebSelector.sendToTop();

        if (prospectRequestId === this.prospectRequestId && this.prospectsGeoJson) {
          this.prospectsMapSectionService.loadLocationsGeoJson(this.prospectsGeoJson);
        }
        this.prospectsMapSectionService.sendLocationsToTop();

        await this.mapHelperService.waitForMapToRender();
        if (requestId !== this.zoneRequestId) {
          return;
        }
      }
    } finally {
      if (requestId === this.zoneRequestId) {
        this.isLoadingZone = false;
        this.prospectsMapSectionService.setLoading(false);
      }
    }
  }

  reloadProspectLayer(categoryId: number, filters: CategoryFiltersDTO[], isForceReload: boolean = false): void {
    if (!this.secondZone.isSelected) {
      return;
    }
    
    this.destroyProspectLoad$.next();
    this.isLoadingZone = true;
    const requestId = ++this.prospectRequestId;

    this.prospectsMapSectionService.setLoading(true);
    this.GetLayerByIdLocationCategoryGeoJSON(categoryId, filters, isForceReload).pipe(takeUntil(this.destroyProspectLoad$)).subscribe({
      next: (dataParsed) => {
        if (requestId !== this.prospectRequestId) {
          return;
        }
        this.prospectsGeoJson = dataParsed;
        if (this.prospectsGeoJson) {
          this.prospectsMapSectionService.loadLocationsGeoJson(this.prospectsGeoJson);
        }
        this.prospectsMapSectionService.sendLocationsToTop();
        this.prospectsMapSectionService.setLoading(false);
        this.isLoadingZone = false;
      },
      error: (error) => {
        if (requestId === this.prospectRequestId) {
          this.prospectsMapSectionService.setLoading(false);
          this.isLoadingZone = false;
        }
        console.log('error recargando capa de prospectos', error);
      }
    });
  }

  private cancelPendingZoneLoad(): void {
    this.destroyZoneLoad$.next();
    this.destroyProspectLoad$.next();

    this.prospectsMapSectionService.setLoading(false);
    this.isLoadingZone = false;
  }

  resetZones(): void {
    this.firstZone = { name: 'Región Seleccionada', isSelected: false };
    this.secondZone = { name: 'Zona Seleccionada', isSelected: false, id: null };
    this.resetZoneData();
  }

  resetZoneData(): void {
    this.zoneRequestId++;
    this.prospectRequestId++;
    this.cancelPendingZoneLoad();

    this.agebsGeoJson = null;
    this.prospectsGeoJson = null;

    if (this.polygonSelector) {
      this.isPolygonSelectionWithevelsActive = true;
    }

    if (this.agebSelector) {
      this.agebSelector.clearGeoJson();
      this.agebSelector.deactivate();
    }
    this.prospectsMapSectionService.clearLocations();
    this.prospectsMapSectionService.resetFilters();
    this.locationHash.clear();
  }

  async goBack(): Promise<void> {
    if (this.secondZone.isSelected) {
      this.secondZone = { name: 'Zona Seleccionada', isSelected: false, id: null };
      this.resetZoneData();

      // Reactivar el selector de polígonos con niveles
      if (this.polygonSelector) {
        this.isPolygonSelectionWithevelsActive = true;
        this.polygonSelector.restoreFullGeoJsonForCurrentLevel();
      }
    } else {
      if (this.polygonSelector) {
        await this.polygonSelector.goToPreviousLevel();
      }
    }
  }

  async resetToStart(): Promise<void> {
    this.resetZones();
    this.mapHelperService.FlyToCamera(
      this.mapHelperService.Lat.toString(),
      this.mapHelperService.Lng.toString(),
      this.mapHelperService.Zoom.toString(),
      0
    );

    if (this.polygonSelector) {
      const resetAll = false;
      await this.polygonSelector.resetToInitialLevel(resetAll);
    }
  }

  GetLayerByIdLocationZoneGeoJSON(zoneName: string, selectedZoneId: string): Observable<FeatureCollection<Polygon | MultiPolygon>> {
    return this.layersAPIService.GetLayerDataFilteredByNameGeojson(zoneName, selectedZoneId).pipe(
      take(1),
      map(geoResp => this.parseAgebGeoJson(geoResp))
    );
  }

  parseAgebGeoJson(geoResp: any): FeatureCollection<Polygon | MultiPolygon> {
    var geoJson: FeatureCollection<Polygon | MultiPolygon> = null;
    try {
      if (geoResp.isSuccess) {
        const dataArray = JSON.parse(geoResp.result) as { [key: string]: any }[];

        const features: GeoJSON.Feature<Polygon | MultiPolygon>[] = [];

        dataArray.forEach((item, index) => {
          if (item.GeomJson) {
            try {
              let geometry: any = null;

              // va a checar si es wk o es GeoJson, si es geojson lo parsea directo, si es wk usa el wellknown
              const geomStr = item.GeomJson.trim();
              if (geomStr.startsWith('{') || geomStr.startsWith('[')) {
                geometry = JSON.parse(geomStr);
              } else if (geomStr.match(/^(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)/i)) {
                geometry = wellknown.parse(geomStr);
                if (!geometry) {
                  console.log("selector, error parseando wellknown para el item ", index, geomStr);
                  return;
                }
              } else {
                console.log("selector, formato de geometría desconocido para el item ", index, geomStr);
                return;
              }

              const properties = { ...item };
              delete properties['GeomJson'];

              const feature: GeoJSON.Feature<Polygon | MultiPolygon> = {
                type: 'Feature',
                id: index + 1,
                geometry: geometry,
                properties: properties
              };

              features.push(feature);
            } catch (parseError) {
              console.log(`selector, error parseando el diccionario`, parseError);
            }
          }
        });

        geoJson = {
          type: 'FeatureCollection',
          features: features
        };

      } else {
        console.log("selector, no hubo respuesta del servicdor");
        Sentry.captureMessage('ProspectsSectionComponent.loadAgebGeoJson: isSuccess false', { level: 'warning', extra: { response: geoResp } });
      }
    } catch (error) {
      console.log("selector, error parseando el geojson", error);
    }

    return geoJson;
  }

  GetLayerByIdLocationCategoryGeoJSON(categoryId: number, filters: CategoryFiltersDTO[], isForceReload: boolean = false): Observable<FeatureCollection<Point>> {
    return this.layersAPIService.GetLayerByIdLocationCategoryGeoJSON(categoryId, filters, isForceReload).pipe(
      take(1),
      map(geoResp => this.parseProspectsGeoJson(geoResp))
    );
  }

  parseProspectsGeoJson(geoResp: any): FeatureCollection<Point> {
    var geoJson: FeatureCollection<Point> = null;
    try {
      if (geoResp.isSuccess) {
        geoJson = JSON.parse(geoResp.result) as FeatureCollection<Point>;

        this.locationHash.clear();
        geoJson.features.forEach((feature) => {
          if(feature.properties && feature.properties['_Id']){
            this.locationHash.set(feature.properties['_Id'], { lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] });
          }
        });
      } else {
        console.log("selector, no hubo respuesta del servicdor");
        Sentry.captureMessage('ProspectsSectionComponent.loadProspectsGeoJson: isSuccess false', { level: 'warning', extra: { response: geoResp } });
      }
    } catch (error) {
      console.log("selector, error parseando el geojson", error);
    }

    return geoJson;
  }
}
